import axios from 'axios'
import { useTranslation } from 'react-i18next'

import { setChoice } from '@/api/choices'
import { createConsultation, createFollowUpConsultation } from '@/api/consultations'
import { getNextModule } from '@/api/modules'
import { setReminder } from '@/api/reminders'
import { resetTraces } from '@/api/traces'
import { createUser, getRouteConditions, setUserInfo, updateUser } from '@/api/user'
import { UserFormData } from '@/contexts/userForm'
import { useChoices } from '@/hooks/useChoices'
import { useConsultationStep } from '@/hooks/useConsultationStep'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useGeneralErrors } from '@/hooks/useGeneralErrors'
import { useLoading } from '@/hooks/useLoading'
import { useModal } from '@/hooks/useModal'
import { useModule } from '@/hooks/useModule'
import { useNavigate } from '@/hooks/useNavigate'
import { useQuestion } from '@/hooks/useQuestion'
import { useReminder } from '@/hooks/useReminder'
import { useStep } from '@/hooks/useStep'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import { useUserForm } from '@/hooks/useUserForm'
import i18n from '@/locale/i18n'
import { ACTION_TYPE, ROUTE_CONDITION_TYPE } from '@/types/action'
import { BUTTON_ACTION_TYPE, ButtonAction, ButtonComponent, COMPONENT_TYPE, FormComponents, Row } from '@/types/components'
import { CONSULTATION_STEPS } from '@/types/consultation'
import { ERROR_CODE, ERROR_TYPES } from '@/types/error'
import { AVAILABLE_LANGUAGES } from '@/types/language'
import { STEP_TYPE } from '@/types/step'
import { User } from '@/types/user'
import { getRouteByConditions } from '@/utils/route'

import { useConsultation } from './useConsultation'

export const useButtonClick = () => {
  const { t } = useTranslation()
  const { setReminderSet } = useReminder()
  const { navigateToStep, navigateToModule, returnToStep } = useNavigate()
  const { currentStep } = useStep()
  const { currentModule } = useModule()
  const { traces } = useTraces()
  const { open: openModal, close: closeModal } = useModal()
  const { getUserDataById, getUserData } = useUserForm()
  const { user, updateUser: updateUserObject } = useUser()
  const { choices } = useChoices()
  const { addGeneralError, clearGeneralErrors } = useGeneralErrors()
  const { formErrors } = useFormErrors()
  const { addFormError, setFormErrors } = useFormErrors()
  const { showLoading, hideLoading } = useLoading()
  const { currentConsultationStep, nextStep: nextConsultationStep } = useConsultationStep()
  const { question, resetQuestion } = useQuestion()
  const { updateConsultation } = useConsultation()

  const validateComponent = (component: FormComponents) => {
    const { id, required } = component
    const currentFieldError = formErrors ? formErrors[id] : null

    if (currentFieldError) {
      return id
    }

    const value = component.componentType === COMPONENT_TYPE.COUNTRY_SELECT ? getUserDataById('country') : getUserDataById(id)
    const choice = choices?.find(({ component, stepId }) => component.id === id && currentStep?.id === stepId)

    if (required && !value && !choice) {
      addFormError(id, 'error.validation.required')

      return id
    }

    return null
  }

  const validateForm = () => {
    if (!currentStep) throw new Error('No current step found')

    switch (currentStep?.type) {
      case STEP_TYPE.CHOICES:
      case STEP_TYPE.USER_FORM: {
        const errorComponents = currentStep?.componentsOrder.map((componentId) => {
          const component = currentStep?.components[componentId] as FormComponents | Row

          if (component.componentType === COMPONENT_TYPE.ROW) {
            // If component is a row, we need to validate each component inside the row
            const errorComponents = component.components.map((rowComponent) => validateComponent(rowComponent as FormComponents))

            return errorComponents.some(Boolean)
          }

          return validateComponent(component)
        })

        return !errorComponents.some(Boolean)
      }

      case STEP_TYPE.CONSULTATION: {
        if (!question.question) {
          addFormError('question', 'error.validation.required')
          return false
        }

        return !formErrors?.question
      }

      default: {
        return true
      }
    }
  }

  const handleUserAction = async (action: ACTION_TYPE.CREATE_USER | ACTION_TYPE.UPDATE_USER | ACTION_TYPE.SET_USER_INFO) => {
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')

    try {
      const payload = {
        user: {
          ...user.data, // Use current user data
          ...getUserData() // Override user data with form data
        }
      }

      let userObj: User | null
      switch (action) {
        case ACTION_TYPE.CREATE_USER:
          userObj = await createUser(payload)
          break

        case ACTION_TYPE.UPDATE_USER:
          userObj = await updateUser(payload)
          break

        case ACTION_TYPE.SET_USER_INFO: {
          if (!currentStep) throw new Error('No current step found')
          // Only send user data filled on the current step
          const currentStepUserData = Object.values(currentStep.components).reduce((acc, component) => {
            if (component.componentType === COMPONENT_TYPE.ROW) {
              // If component is a row, we need to check each component inside the row
              const rowComponents = (component as Row).components as FormComponents[]
              rowComponents.forEach((rowComponent) => {
                acc[rowComponent.id] = getUserDataById(rowComponent.id)
              })
            } else {
              acc[component.id] = getUserDataById(component.id)
            }

            return acc
          }, {} as { [key: string]: UserFormData })

          userObj = await setUserInfo({
            uniqueId: user.data.uniqueId,
            userId: user.data.userId,
            ...currentStepUserData
          })
          break
        }

        default:
          userObj = null
          break
      }

      if (!userObj) throw new Error('No user found')

      updateUserObject(userObj)
      return userObj
    } catch (e) {
      console.error(e)
      if (axios.isAxiosError(e)) {
        if (e.response?.data?.type === ERROR_CODE.USER_FIELDS_ERROR) {
          console.error('form errors', e)
          const formErrors = e.response?.data?.message
          if (!formErrors) throw new Error('No form errors found')

          const parsedFormErrors = formErrors.reduce(
            (
              acc: {
                [key: string]: string
              },
              error: {
                field: string
                message: {
                  type: string
                }[]
              }
            ) => {
              acc[error.field] = error.message[0].type
              return acc
            },
            {}
          )

          setFormErrors(parsedFormErrors)

          const errorStepId = traces.find((trace) =>
            Object.keys(parsedFormErrors).some((errorField) => currentModule?.steps[trace.stepId].components[errorField])
          )?.stepId

          if (!errorStepId) throw new Error('No step found for error field')

          returnToStep(errorStepId)
          return
        }
      }

      throw new Error('Errors found on user form')
    }
  }

  const handleSetReminder = async (
    action: ACTION_TYPE.SET_REMINDER_PHYSICIAN | ACTION_TYPE.SET_REMINDER_PHARMACY | ACTION_TYPE.SET_REMINDER_WEEKLY
  ) => {
    if (!currentStep) throw new Error('No current step found')
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')

    const choice = choices.find((choice) => choice.stepId === currentStep?.id)
    const option = choice?.options ? choice.options[0] : null

    await setReminder({
      uniqueId: user?.data?.uniqueId,
      reminder: option?.value.key,
      action
    })

    setReminderSet(option?.value.key !== undefined && option?.value.key !== 'not-remind')
  }

  const handleSetChoice = async () => {
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')
    if (!choices || choices.length === 0) throw new Error('No choices found')

    const lastChoice = choices[choices.length - 1]

    await setChoice({
      uniqueId: user?.data?.uniqueId,
      choice: lastChoice
    })
  }

  const handleGetRouteConditions = async (type: ROUTE_CONDITION_TYPE) => {
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')

    const userWithUpdatedRouteConditions = await getRouteConditions({
      uniqueId: user?.data?.uniqueId,
      type
    })

    return userWithUpdatedRouteConditions
  }

  const handleNavigationPreviousActions = async (
    buttonAction: ButtonAction<
      BUTTON_ACTION_TYPE.NEXT_STEP | BUTTON_ACTION_TYPE.NEXT_MODULE | BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM | BUTTON_ACTION_TYPE.RELOAD_PAGE
    >
  ) => {
    if (!buttonAction.previousActions) throw new Error('No previous actions found')

    let updatedUser: User | null = user

    for (const previousAction of buttonAction.previousActions) {
      // TODO this is for retro-compatibility, remove on next release
      const legacyPreviousAction = typeof previousAction === 'string' ? { action: previousAction } : previousAction

      switch (legacyPreviousAction.action) {
        case ACTION_TYPE.CREATE_USER:
        case ACTION_TYPE.UPDATE_USER:
        case ACTION_TYPE.SET_USER_INFO: {
          updatedUser = (await handleUserAction(legacyPreviousAction.action)) ?? null
          break
        }

        case ACTION_TYPE.GET_ROUTE_CONDITIONS: {
          updatedUser = await handleGetRouteConditions(legacyPreviousAction.type)
          break
        }

        case ACTION_TYPE.SET_REMINDER_PHYSICIAN:
        case ACTION_TYPE.SET_REMINDER_PHARMACY:
        case ACTION_TYPE.SET_REMINDER_WEEKLY: {
          await handleSetReminder(legacyPreviousAction.action)
          break
        }

        case ACTION_TYPE.SET_CHOICE: {
          await handleSetChoice()
          break
        }

        case ACTION_TYPE.CREATE_CONSULTATION: {
          const createdConsultation = await createConsultation({
            question: question.question,
            attachments: question.attachments,
            uniqueId: user?.data?.uniqueId as string
          })

          updateConsultation(createdConsultation)
          resetQuestion() // Reset question

          break
        }

        case ACTION_TYPE.CREATE_FOLLOW_UP_CONSULTATION: {
          const createdFollowUpConsultation = await createFollowUpConsultation({
            question: question.question,
            attachments: question.attachments,
            uniqueId: user?.data?.uniqueId as string
          })

          updateConsultation(createdFollowUpConsultation)
          resetQuestion() // Reset question

          break
        }

        case ACTION_TYPE.RESET_TRACES: {
          // Remove traces from local storage
          localStorage.removeItem('traces')

          // Reset traces on BE
          const updatedUser = await resetTraces({
            uniqueId: user?.data?.uniqueId as string
          })
          updateUserObject(updatedUser)

          window.location.reload()
          break
        }
      }
    }

    return updatedUser
  }

  const handleNavigation = async (
    buttonAction: ButtonAction<
      BUTTON_ACTION_TYPE.NEXT_STEP | BUTTON_ACTION_TYPE.NEXT_MODULE | BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM | BUTTON_ACTION_TYPE.RELOAD_PAGE
    >
  ) => {
    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')

    const updatedUser = buttonAction.previousActions ? await handleNavigationPreviousActions(buttonAction) : user

    switch (buttonAction.type) {
      case BUTTON_ACTION_TYPE.NEXT_MODULE: {
        const requestModule = getRouteByConditions({
          userConditions: updatedUser?.routeConditions,
          routes: buttonAction.routes
        })?.route?.module

        if (!requestModule) throw new Error('No module matching conditions found')
        if (!updatedUser?.data?.uniqueId) throw new Error('No user uniqueId found')

        const { module: nextModule } = await getNextModule({
          uniqueId: updatedUser?.data?.uniqueId,
          moduleId: requestModule
        })

        return navigateToModule(nextModule)
      }

      case BUTTON_ACTION_TYPE.NEXT_STEP: {
        const { route } = getRouteByConditions({
          userConditions: updatedUser?.routeConditions,
          routes: buttonAction.routes
        })

        if (!route?.step) throw new Error('No route or step found')

        return navigateToStep(route.step)
      }

      case BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM: {
        nextConsultationStep()
      }
    }
  }

  const handleButtonClick = async (buttonAction: ButtonComponent['buttonAction']) => {
    clearGeneralErrors()
    showLoading()
    setReminderSet(false)

    try {
      switch (buttonAction?.type) {
        case BUTTON_ACTION_TYPE.SET_LANGUAGE: {
          localStorage.setItem('languageSelected', buttonAction.language)
          await i18n.changeLanguage(buttonAction.language)
          return window.location.reload()
        }

        case BUTTON_ACTION_TYPE.OPEN_MODAL: {
          return openModal(buttonAction.modalId)
        }

        case BUTTON_ACTION_TYPE.CLOSE_MODAL: {
          return closeModal()
        }

        case BUTTON_ACTION_TYPE.NEXT_STEP:
        case BUTTON_ACTION_TYPE.NEXT_MODULE:
        case BUTTON_ACTION_TYPE.RELOAD_PAGE:
        case BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM: {
          const stepType = currentStep?.type as STEP_TYPE

          if (
            [STEP_TYPE.USER_FORM, STEP_TYPE.CHOICES].includes(stepType) ||
            [CONSULTATION_STEPS.LANDING, CONSULTATION_STEPS.FORM].includes(currentConsultationStep as CONSULTATION_STEPS)
          ) {
            const validForm = validateForm()

            if (!validForm) break
          }

          await handleNavigation(buttonAction)
          break
        }

        case BUTTON_ACTION_TYPE.OPEN_LINK: {
          const selectedLanguage = i18n.language as AVAILABLE_LANGUAGES
          const url = buttonAction.languages?.[selectedLanguage] ? buttonAction.languages[selectedLanguage] : buttonAction.href

          return window.open(url, '_blank')
        }
      }
    } catch (error: any) {
      // TODO type error
      console.error('handleButtonClick:error', { error, cause: error?.cause })

      // if (error?.cause !== ERROR_CAUSES.API) throw error

      return addGeneralError({
        type: ERROR_TYPES.GENERAL,
        message: error?.message ? t(error?.message) : 'Something went wrong, please try again later'
      })
    } finally {
      // Only hide loading if button action is not reload page
      if (buttonAction?.type !== BUTTON_ACTION_TYPE.RELOAD_PAGE) hideLoading()
    }
  }

  return {
    handleButtonClick
  }
}
